import { db } from '../../sdk_initialization';
import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { sfVendorRegistration, getFunctionConfig } from './defaultsService';

const COLLECTION = 'attendees';

/**
 * Add a new attendee
 * NOTE: Attendees are tied to vendorId (the vendor entity), not individual user profileId.
 * The vendorId represents the vendor who owns this attendee, while createdByProfileId tracks who created it.
 * @param {Object} attendeeData - { eventId, vendorId, vendorNumber, firstName, lastName, yob, gender, createdByProfileId }
 * @returns {Promise<string>} - The ID of the created attendee
 */
export async function addAttendee(attendeeData) {
  let isPrimary = false;
  let checkedIn = false;

  // Only the first attendee for an event+vendor can be primary.
  if (attendeeData?.eventId && attendeeData?.vendorId) {
    const existingQ = query(
      collection(db, COLLECTION),
      where('eventId', '==', attendeeData.eventId),
      where('vendorId', '==', attendeeData.vendorId)
    );
    const existingSnap = await getDocs(existingQ);

    if (existingSnap.empty) {
      isPrimary = true;
    } else {
      const existingDocs = existingSnap.docs;
      const hasExplicitPrimary = existingDocs.some(d => d.data()?.isPrimary === true);
      const legacyPrimaryDoc = existingDocs.find(d => {
        const data = d.data();
        const idxRaw = data?.index ?? data?.attendeeIndex ?? data?.sequenceIndex ?? data?.rowIndex;
        const idx = Number(idxRaw);
        return Number.isFinite(idx) && idx === 0;
      });

      if (!hasExplicitPrimary) {
        let primaryDocToPromote = legacyPrimaryDoc;
        if (!primaryDocToPromote) {
          primaryDocToPromote = existingDocs
            .slice()
            .sort((a, b) => {
              const aVal = a.data()?.createdAt;
              const bVal = b.data()?.createdAt;
              const aMs = typeof aVal?.toDate === 'function'
                ? aVal.toDate().getTime()
                : (typeof aVal?.seconds === 'number' ? aVal.seconds * 1000 : 0);
              const bMs = typeof bVal?.toDate === 'function'
                ? bVal.toDate().getTime()
                : (typeof bVal?.seconds === 'number' ? bVal.seconds * 1000 : 0);
              return aMs - bMs;
            })[0];
        }

        if (primaryDocToPromote) {
          await updateDoc(primaryDocToPromote.ref, {
            isPrimary: true,
            updatedAt: Timestamp.now(),
          });
        }
      }
    }
  }

  // If vendor is already checked in, auto-mark new attendee as checked in.
  if (attendeeData?.eventId && attendeeData?.vendorId) {
    try {
      const checkinId = `${attendeeData.eventId}_${attendeeData.vendorId}`;
      const checkinSnap = await getDoc(doc(db, 'checkins', checkinId));
      if (checkinSnap.exists()) {
        const checkinData = checkinSnap.data() || {};
        const vendorCheckedIn = checkinData.checkedIn === true || (checkinData.checkedIn == null && Boolean(checkinData.checkinAt));
        const vendorCheckedOut = checkinData.checkedOut === true || (checkinData.checkedOut == null && Boolean(checkinData.checkoutAt));
        if (vendorCheckedIn && !vendorCheckedOut) {
          checkedIn = true;
        }
      }
    } catch {
      // Non-fatal. If lookup fails, attendee defaults to not checked in.
    }
  }

  const docData = {
    ...attendeeData,
    isPrimary,
    checkedIn,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  const docRef = await addDoc(collection(db, COLLECTION), docData);
  const attendeeId = docRef.id;

  // Notify Salesforce about vendor update (non-blocking)
  getFunctionConfig()
    .then(appSettings => {
      if (attendeeData.eventId && attendeeData.vendorId) {
        sfVendorRegistration(appSettings, attendeeData.eventId, attendeeData.vendorId).catch(err => {
          if (appSettings.enableLogs) console.warn('[addAttendee] sfVendorRegistration failed (non-blocking):', err && err.message ? err.message : err);
        });
      }
    })
    .catch(() => {});

  return attendeeId;
}

/**
 * Get a single attendee by ID
 */
export async function getAttendee(id) {
  const docRef = doc(db, COLLECTION, id);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * List all attendees (admin/organizer use)
 */
export async function listAllAttendees() {
  const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * List attendees for a specific vendor (by vendorId)
 */
export async function listAttendeesByVendor(vendorId) {
  const q = query(
    collection(db, COLLECTION),
    where('vendorId', '==', vendorId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * List attendees for a specific event
 */
export async function listAttendeesByEvent(eventId) {
  const q = query(
    collection(db, COLLECTION),
    where('eventId', '==', eventId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * List attendees for a specific event and vendor
 */
export async function listAttendeesByEventAndVendor(eventId, vendorId) {
  const q = query(
    collection(db, COLLECTION),
    where('eventId', '==', eventId),
    where('vendorId', '==', vendorId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Update an attendee
 */
export async function updateAttendee(id, updates) {
  const docRef = doc(db, COLLECTION, id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });

  // Attempt to notify Salesforce about vendor update (non-blocking)
  (async () => {
    try {
      let eventId = updates.eventId;
      let vendorId = updates.vendorId;
      if (!eventId || !vendorId) {
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          eventId = eventId || data.eventId;
          vendorId = vendorId || data.vendorId;
        }
      }
      if (eventId && vendorId) {
        const appSettings = await getFunctionConfig();
        sfVendorRegistration(appSettings, eventId, vendorId).catch(err => {
          if (appSettings.enableLogs) console.warn('[updateAttendee] sfVendorRegistration failed (non-blocking):', err && err.message ? err.message : err);
        });
      }
    } catch (e) {
      // non-blocking
    }
  })();
}

/**
 * Delete an attendee
 */
export async function deleteAttendee(id) {
  const docRef = doc(db, COLLECTION, id);
  // Read doc to get vendor/event info for SF notification
  let eventId;
  let vendorId;
  try {
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data();
      if (data?.isPrimary === true) {
        throw new Error('Cannot delete primary attendee');
      }
      eventId = data.eventId;
      vendorId = data.vendorId;
    }
  } catch (e) {
    if (e && e.message && e.message.includes('primary attendee')) throw e;
  }

  // If we could determine eventId/vendorId, ensure this is not the last attendee
  if (eventId && vendorId) {
    try {
      const count = await countAttendeesByEventAndVendor(eventId, vendorId);
      if (count <= 1) {
        throw new Error('Cannot delete the last attendee for this vendor and event');
      }
    } catch (e) {
      // If count lookup failed, proceed cautiously and bubble up the error
      if (e && e.message && e.message.startsWith('Cannot delete the last')) throw e;
    }
  }

  await deleteDoc(docRef);

  // Notify Salesforce about vendor update (non-blocking)
  if (eventId && vendorId) {
    getFunctionConfig()
      .then(appSettings => {
        sfVendorRegistration(appSettings, eventId, vendorId).catch(err => {
          if (appSettings.enableLogs) console.warn('[deleteAttendee] sfVendorRegistration failed (non-blocking):', err && err.message ? err.message : err);
        });
      })
      .catch(() => {});
  }
}

/**
 * Count attendees for a specific event and vendor
 */
export async function countAttendeesByEventAndVendor(eventId, vendorId) {
  const attendees = await listAttendeesByEventAndVendor(eventId, vendorId);
  return attendees.length;
}
